import type { Track } from '../track/TrackLoader';
import type { Checkpoint } from '../track/Checkpoints';

const W = 218;
const H = 176;
const PADDING = 18;
const SECTOR_COLORS = [
  'rgba(32, 224, 255, 0.96)',
  'rgba(105, 225, 255, 0.88)',
  'rgba(124, 241, 208, 0.9)',
] as const;
const SECTOR_LABELS = ['S1', 'S2', 'S3'] as const;

export interface MinimapProjection {
  offX: number;
  offZ: number;
  scale: number;
}

type SectorIndex = 0 | 1 | 2;

interface MapPoint {
  x: number;
  z: number;
}

export class Minimap {
  readonly root: HTMLElement;
  readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly trackImage: HTMLCanvasElement;
  private readonly track: Track;
  private readonly checkpoints: readonly Checkpoint[];
  private readonly bounds: { minX: number; maxX: number; minZ: number; maxZ: number };
  private readonly scale: number;
  private readonly offX: number;
  private readonly offZ: number;

  constructor(parent: HTMLElement, track: Track, checkpoints: readonly Checkpoint[] = []) {
    this.track = track;
    this.checkpoints = checkpoints;
    this.root = document.createElement('section');
    this.root.id = 'tst-minimap-panel';
    this.root.className = 'hud-map-panel hud-map-floating';
    this.root.hidden = true;

    this.canvas = document.createElement('canvas');
    this.canvas.id = 'tst-minimap';
    this.canvas.width = W;
    this.canvas.height = H;
    this.canvas.hidden = true;
    this.root.append(this.canvas);
    parent.appendChild(this.root);
    this.ctx = this.canvas.getContext('2d')!;

    this.bounds = computeBounds(track);
    const trackW = this.bounds.maxX - this.bounds.minX;
    const trackH = this.bounds.maxZ - this.bounds.minZ;
    const usableW = W - PADDING * 2;
    const usableH = H - PADDING * 2;
    this.scale = Math.min(usableW / trackW, usableH / trackH);
    this.offX = PADDING + (usableW - trackW * this.scale) / 2 - this.bounds.minX * this.scale;
    this.offZ = PADDING + (usableH - trackH * this.scale) / 2 - this.bounds.minZ * this.scale;

    this.trackImage = this.bakeTrackImage();
  }

  show(): void {
    this.root.hidden = false;
    this.canvas.hidden = false;
  }

  hide(): void {
    this.root.hidden = true;
    this.canvas.hidden = true;
  }

  /** Redraw with current racer position and heading. */
  update(racerX: number, racerZ: number, heading: number, sectorIndex: SectorIndex): void {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(this.trackImage, 0, 0);
    this.drawSectorLabels(ctx, sectorIndex);

    const px = this.worldToMapX(racerX);
    const pz = this.worldToMapZ(racerZ);
    const arrow = __test__mapHeadingEndpoint(racerX, racerZ, heading, this.projection(), 8);
    const angle = Math.atan2(arrow.z - pz, arrow.x - px);

    ctx.save();
    ctx.translate(px, pz);
    ctx.rotate(angle);
    ctx.fillStyle = 'rgba(255, 159, 37, 0.96)';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 3;
    ctx.beginPath();
    ctx.moveTo(5.8, 0);
    ctx.lineTo(-3.8, -3.4);
    ctx.lineTo(-1.8, 0);
    ctx.lineTo(-3.8, 3.4);
    ctx.closePath();
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(255, 232, 194, 0.78)';
    ctx.lineWidth = 0.9;
    ctx.stroke();
    ctx.restore();
  }

  private bakeTrackImage(): HTMLCanvasElement {
    const c = document.createElement('canvas');
    c.width = W;
    c.height = H;
    const ctx = c.getContext('2d')!;

    this.drawSectorSegments(ctx);
    this.drawSectorBreaks(ctx);

    return c;
  }

  private drawSectorSegments(ctx: CanvasRenderingContext2D): void {
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const [firstBreak, secondBreak] = this.sectorBoundaryIndices();
    const ranges: Array<{ sector: SectorIndex; start: number; end: number }> = [
      { sector: 0, start: 0, end: firstBreak },
      { sector: 1, start: firstBreak, end: secondBreak },
      { sector: 2, start: secondBreak, end: 0 },
    ];

    for (const range of ranges) {
      const points = this.mapPointsBetween(range.start, range.end);
      if (points.length < 2) continue;
      ctx.strokeStyle = SECTOR_COLORS[range.sector];
      ctx.lineWidth = 1.9;
      ctx.shadowColor = 'rgba(0, 198, 255, 0.28)';
      ctx.shadowBlur = 5;
      traceOpenPath(ctx, points);
      ctx.stroke();
    }
    ctx.shadowBlur = 0;
    ctx.lineCap = 'butt';
  }

  private drawSectorBreaks(ctx: CanvasRenderingContext2D): void {
    const boundaries = this.checkpoints
      .filter((cp) => cp.sectorIndex === 0 || cp.sectorIndex === 1)
      .sort((a, b) => a.sectorIndex - b.sectorIndex);
    for (const boundary of boundaries) {
      const x = this.worldToMapX(boundary.x);
      const z = this.worldToMapZ(boundary.z);
      const routeDx = this.worldToMapX(boundary.x + boundary.tx) - x;
      const routeDz = this.worldToMapZ(boundary.z + boundary.tz) - z;
      const len = Math.hypot(routeDx, routeDz) || 1;
      const nx = -routeDz / len;
      const nz = routeDx / len;
      const tick = 5.2;

      ctx.strokeStyle = 'rgba(248, 251, 255, 0.72)';
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.58)';
      ctx.shadowBlur = 3;
      ctx.beginPath();
      ctx.moveTo(x - nx * tick, z - nz * tick);
      ctx.lineTo(x + nx * tick, z + nz * tick);
      ctx.stroke();
    }
    ctx.shadowBlur = 0;
    ctx.lineCap = 'butt';
  }

  private drawSectorLabels(ctx: CanvasRenderingContext2D, currentSector: SectorIndex): void {
    ctx.save();
    ctx.font = '700 9px "Share Tech Mono", ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.74)';
    ctx.shadowBlur = 4;
    for (let sector = 0; sector < 3; sector++) {
      const sample = sampleTrackAtArcLength(
        this.track,
        ((sector + 0.5) * this.track.totalLength) / 3,
      );
      const x = this.worldToMapX(sample.x);
      const z = this.worldToMapZ(sample.z);
      const routeDx = this.worldToMapX(sample.x + sample.tx) - x;
      const routeDz = this.worldToMapZ(sample.z + sample.tz) - z;
      const len = Math.hypot(routeDx, routeDz) || 1;
      const nx = -routeDz / len;
      const nz = routeDx / len;
      const side = sector === 1 ? -1 : 1;
      const labelX = clamp(x + nx * 10 * side, 8, W - 8);
      const labelZ = clamp(z + nz * 10 * side, 8, H - 8);
      ctx.fillStyle = sector === currentSector
        ? 'rgba(248, 251, 255, 0.92)'
        : 'rgba(135, 220, 255, 0.58)';
      ctx.fillText(SECTOR_LABELS[sector], labelX, labelZ);
    }
    ctx.restore();
  }

  private sectorBoundaryIndices(): [number, number] {
    const n = this.track.centerline.length;
    const first = this.checkpoints.find((cp) => cp.sectorIndex === 0)?.centerlineIndex
      ?? indexAtArcLength(this.track, this.track.totalLength / 3);
    const second = this.checkpoints.find((cp) => cp.sectorIndex === 1)?.centerlineIndex
      ?? indexAtArcLength(this.track, (this.track.totalLength * 2) / 3);
    return [
      clamp(Math.round(first), 1, Math.max(1, n - 2)),
      clamp(Math.round(second), Math.min(n - 1, first + 1), Math.max(1, n - 1)),
    ];
  }

  private mapPointsBetween(start: number, end: number): MapPoint[] {
    const points: MapPoint[] = [];
    const n = this.track.centerline.length;
    let i = start;
    for (let guard = 0; guard <= n; guard++) {
      const p = this.track.centerline[i];
      points.push({ x: this.worldToMapX(p.x), z: this.worldToMapZ(p.z) });
      if (i === end) break;
      i = (i + 1) % n;
    }
    return points;
  }

  private worldToMapX(x: number): number {
    return __test__worldToMapX(x, this.projection());
  }

  /** World +Z (forward at start) renders to map -Y so the track points up in the minimap. */
  private worldToMapZ(z: number): number {
    return __test__worldToMapZ(z, this.projection());
  }

  private projection(): MinimapProjection {
    return { offX: this.offX, offZ: this.offZ, scale: this.scale };
  }
}

function computeBounds(track: Track): { minX: number; maxX: number; minZ: number; maxZ: number } {
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (const p of track.centerline) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.z < minZ) minZ = p.z;
    if (p.z > maxZ) maxZ = p.z;
  }
  return { minX, maxX, minZ, maxZ };
}

export function __test__worldToMapX(x: number, projection: MinimapProjection): number {
  return W - (projection.offX + x * projection.scale);
}

export function __test__worldToMapZ(z: number, projection: MinimapProjection): number {
  return H - (projection.offZ + z * projection.scale);
}

export function __test__mapHeadingEndpoint(
  racerX: number,
  racerZ: number,
  heading: number,
  projection: MinimapProjection,
  length: number,
): { x: number; z: number } {
  return {
    x: __test__worldToMapX(racerX + Math.sin(heading) * length, projection),
    z: __test__worldToMapZ(racerZ + Math.cos(heading) * length, projection),
  };
}

function traceOpenPath(ctx: CanvasRenderingContext2D, points: readonly MapPoint[]): void {
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].z);
  for (let i = 1; i < points.length - 1; i++) {
    const current = points[i];
    const next = points[i + 1];
    ctx.quadraticCurveTo(current.x, current.z, (current.x + next.x) / 2, (current.z + next.z) / 2);
  }
  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.z);
}

function indexAtArcLength(track: Track, target: number): number {
  const arc = normalizeArcLength(target, track.totalLength);
  const arr = track.cumulativeLength;
  let lo = 0;
  let hi = arr.length - 1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (arr[mid] < arc) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function sampleTrackAtArcLength(
  track: Track,
  target: number,
): { x: number; z: number; tx: number; tz: number } {
  const n = track.centerline.length;
  const arc = normalizeArcLength(target, track.totalLength);
  let i = 0;
  while (i < n - 1 && track.cumulativeLength[i + 1] < arc) i++;
  const nextIndex = (i + 1) % n;
  const segStart = track.cumulativeLength[i];
  const segEnd = nextIndex === 0 ? track.totalLength : track.cumulativeLength[nextIndex];
  const t = segEnd === segStart ? 0 : clamp((arc - segStart) / (segEnd - segStart), 0, 1);
  const a = track.centerline[i];
  const b = track.centerline[nextIndex];
  const tangent = track.tangents[i] ?? { tx: 0, tz: 1 };
  return {
    x: a.x + (b.x - a.x) * t,
    z: a.z + (b.z - a.z) * t,
    tx: tangent.tx,
    tz: tangent.tz,
  };
}

function normalizeArcLength(value: number, totalLength: number): number {
  if (!Number.isFinite(value) || totalLength <= 0) return 0;
  return ((value % totalLength) + totalLength) % totalLength;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
